<!-- Keep identical to packages/__shared__/platform-alerts/src/ToastAlert.svelte until merged (ATP-16711) -->
<script lang="ts" module>
  import type { ButtonProps } from "@tastyworks/ui-library"

  export type ToastAction = {
    label: string
    variant?: ButtonProps["variant"]
    onclick: () => void
  }
</script>

<script lang="ts">
  import type { SVGComponent } from "@tastyworks/icons"
  import {
    Alert,
    Button,
    type ComponentTypeProps,
  } from "@tastyworks/ui-library"
  import { toast } from "@zerodevx/svelte-toast"
  import type { Component } from "svelte"

  type AlertRootProps = ComponentTypeProps<typeof Alert.Root>
  type AlertActionsProps = ComponentTypeProps<typeof Alert.Actions>

  interface Props {
    title: string
    description: string
    descriptionItems?: string[]
    variant: AlertRootProps["variant"]
    toastId: number
    dismissible?: boolean
    icon?: SVGComponent
    logo?: Component
    actions?: ToastAction[]
    alignActions?: AlertActionsProps["alignActions"]
  }

  let {
    title,
    description,
    descriptionItems = [],
    variant,
    toastId,
    dismissible = true,
    icon,
    logo: Logo,
    actions = [],
    alignActions,
  }: Props = $props()

  function close() {
    toast.pop(toastId)
  }
</script>

{#snippet customIcon()}
  {#if Logo}
    <Logo />
  {:else}
    <Alert.Icon {icon} />
  {/if}
{/snippet}

<Alert.Root
  {variant}
  {dismissible}
  icon={Logo || icon ? customIcon : undefined}
  ondismiss={close}
  data-testid="toast-alert"
>
  <Alert.Title>
    {title}
  </Alert.Title>

  <Alert.Description>
    {description}
    {#if descriptionItems.length > 0}
      <ul class="mt-1">
        {#each descriptionItems as item}
          <li class="list-['-_']">{item}</li>
        {/each}
      </ul>
    {/if}
  </Alert.Description>

  {#if actions.length > 0}
    <Alert.Actions {alignActions}>
      {#each actions as action, index (index)}
        <Button
          variant={action.variant ?? "primary"}
          onclick={() => {
            action.onclick()
            close()
          }}
        >
          {action.label}
        </Button>
      {/each}
    </Alert.Actions>
  {/if}
</Alert.Root>

<style>
  :global(._toastContainer) {
    /* this will isolate toasts during view-transitions */
    view-transition-name: toast-container;
  }
</style>
